import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { dateTime } from '@/lib/format';
import { getVaultSource } from '@/data/client';
import { getApiToken } from '@/data/api';
import { DOC_TYPE_LABEL } from './labels';

/* One Vault document: its AI summary, where credentials are stored, and its
   text. Opened from a citation, the cited passages are highlighted and
   scrolled into view. */
export function SourceViewer({ sourceId, highlight = [], onClose, actions }: { sourceId: string; highlight?: string[]; onClose: () => void; actions?: ReactNode }) {
  const { data } = useQuery({ queryKey: ['vaultSource', sourceId], queryFn: () => getVaultSource(sourceId) });
  const markRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => { markRef.current?.scrollIntoView({ block: 'center' }); }, [data]);

  // Split the text into plain and highlighted runs from the cited passage ranges.
  const runs = useMemo(() => {
    const text = data?.source.text ?? '';
    const ranges = (data?.chunks ?? []).filter((c) => highlight.includes(c.id)).map((c) => [c.start, c.end] as const).sort((a, b) => a[0] - b[0]);
    const out: { text: string; hit: boolean }[] = [];
    let at = 0;
    for (const [s, e] of ranges) {
      if (s > at) out.push({ text: text.slice(at, s), hit: false });
      out.push({ text: text.slice(Math.max(s, at), e), hit: true });
      at = Math.max(at, e);
    }
    out.push({ text: text.slice(at), hit: false });
    return out;
  }, [data, highlight]);

  const download = async () => {
    const res = await fetch(`/api/vault/sources/${sourceId}/original`, { headers: { Authorization: `Bearer ${await getApiToken()}` } });
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = data?.source.title ?? 'file';
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.source;
  const st = s?.structured;
  let firstHit = true;
  return (
    <div className="lead-drawer">
      <div className="lead-drawer__scrim" onClick={onClose} />
      <aside className="lead-drawer__panel vault-viewer" aria-label="Vault document">
        <div className="lead-drawer__top">
          <div className="lead-drawer__id">
            <div>
              <strong>{s?.title ?? '…'}</strong>
              {s && <span className="crm-cell-sub">{st ? DOC_TYPE_LABEL[st.docType] ?? 'Document' : 'Document'} · updated {dateTime(s.updatedAt)}</span>}
            </div>
          </div>
          <button type="button" className="lead-modal__x" onClick={onClose} aria-label="Close"><Icon name="x" size={20} /></button>
        </div>
        {s && (
          <div className="lead-drawer__content">
            {st && (
              <div className="vault-summary">
                <p>{st.summary}{st.partial ? ' (Very long document: summarised from its opening.)' : ''}</p>
                {st.keyFacts.length > 0 && (
                  <dl className="crm-fields">{st.keyFacts.map((f, i) => <div key={i} style={{ display: 'contents' }}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}</dl>
                )}
                {st.people.length > 0 && <p className="crm-cell-sub">People: {st.people.map((p) => `${p.name}${p.role ? ` (${p.role})` : ''}`).join(', ')}</p>}
                {st.dates.length > 0 && <p className="crm-cell-sub">Dates: {st.dates.map((d) => `${d.date} — ${d.what}`).join('; ')}</p>}
                {(st.brand.colors.length > 0 || st.brand.fonts.length > 0) && (
                  <p className="crm-cell-sub vault-brand">
                    {st.brand.colors.map((c) => <span key={c} className="vault-swatch" style={{ background: /^#|rgb/i.test(c) ? c : undefined }} title={c}>{c}</span>)}
                    {st.brand.fonts.length > 0 && <> Fonts: {st.brand.fonts.join(', ')}</>}
                  </p>
                )}
              </div>
            )}
            {s.redactions.length > 0 && (
              <div className="crm-callout vault-creds">
                <strong><Icon name="lock" size={13} /> Credentials in the original (hidden here)</strong>
                <ul>{s.redactions.map((r, i) => <li key={i}>{r.label}{r.section ? ` — ${r.section}` : ''}</li>)}</ul>
                {s.hasOriginal && <Button variant="outline" size="sm" onClick={() => void download()}>Download original</Button>}
              </div>
            )}
            {s.text
              ? <pre className="vault-text">{runs.map((r, i) => {
                  if (!r.hit) return <span key={i}>{r.text}</span>;
                  const ref = firstHit ? (el: HTMLElement | null) => { markRef.current = el; } : undefined;
                  firstHit = false;
                  return <mark key={i} ref={ref}>{r.text}</mark>;
                })}</pre>
              : <p className="crm-cell-muted">{s.error ?? 'No text yet.'}</p>}
          </div>
        )}
        {(s?.hasOriginal || actions) && (
          <div className="lead-drawer__foot">
            {s?.hasOriginal && s.kind !== 'email' && <Button variant="outline" iconLeft={<Icon name="download" size={15} />} onClick={() => void download()}>Download original</Button>}
            {actions}
          </div>
        )}
      </aside>
    </div>
  );
}
